import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Card from './Card';
import MenuItem from './MenuItem';
import Image from './Image';
import LocalAiAssistant from './LocalAiAssistant';
import RestaurantSetupEntry from './RestaurantSetupEntry';
import { isDeliveryOnly } from '../../services/local/merchantExperience';
import { buildLocalAssistantAccess } from '../../services/local/localAssistantAccess';
import { getInternalAssistantEnvironment } from '../../services/local/internalAssistantRuntime';
import CommissionDebtNotice from './CommissionDebtNotice';
import InicioIcon from '../menu/InicioIcon';
import CocinaIcon from '../menu/CocinaIcon';
import MenuIcon from '../menu/MenuIcon';
import PromosIcon from '../menu/PromosIcon';
import PedidosIcon from '../menu/PedidosIcon';
import MozoIcon from '../menu/MozoIcon';
import IngredientesIcon from '../menu/IngredientesIcon';
import StockIcon from '../menu/StockIcon';
import MiRestauranteIcon from '../menu/MiRestauranteIcon';
import QrMezaIcon from '../menu/QrMezaIcon';
import PagosIcon from '../menu/PagosIcon';
import MzCreditsIcon from '../menu/MzCreditsIcon';
import EstadisticasIcon from '../menu/EstadisticasIcon';
import ConfiguracionIcon from '../menu/ConfiguracionIcon';
import PersonalIcon from '../menu/PersonalIcon';
import { isLocalRuntimeHost } from '../../services/local/runtimeHost';
import { IngredientesProvider } from '../context/IngredientesContext';
import { useAccessControl } from '../context/useAccessControl';
import {
  buildRestaurantProfileStorageKey,
  getRestaurantProfile,
  RESTAURANT_PROFILE_UPDATED_EVENT,
} from '../../services/local/restaurantProfile';
import {
  buildRestaurantBrandTheme,
} from '../../services/local/brandTheme';
import {
  applyHeadBrandingAssets,
  applyInstitutionalBrandingAssets,
} from '../../services/local/institutionalBranding';
import {
  buildOperationalOrdersStorageKey,
  getOperationalOrdersState,
  OPERATIONAL_ORDERS_UPDATED_EVENT,
} from '../../services/local/operationalOrders';
import {
  hydrateSharedOperationalStateToLocal,
  pushSharedBranchClosingControl,
  pushSharedPaymentOperationsState,
  pushSharedWaiterOperationsState,
} from '../../services/supabase/operationalStateService';
import {
  BRANCH_CLOSING_CONTROL_REMOTE_SYNC_SOURCE,
  BRANCH_CLOSING_CONTROL_UPDATED_EVENT,
  BRANCH_CLOSING_EXTENSION_OPTIONS,
  BRANCH_CLOSING_INITIAL_PROMPT_GRACE_MS,
  buildBranchClosingControlStorageKey,
  formatBranchClosingTime,
  getBranchClosingControl,
  getEffectiveBranchOperatingState,
  getMostRecentBranchClosingBoundary,
  isBranchClosingBoundaryHandled,
  saveBranchClosingControl,
  selectNewestBranchClosingControl,
} from '../../services/local/branchClosingControl';
import { hydrateCriticalBranchStateToLocal } from '../../services/supabase/branchRuntimeBootstrap';
import {
  buildWaiterOperationsStorageKey,
  getPendingWaiterServiceRequests,
  getWaiterOperationsState,
  WAITER_OPERATIONS_REMOTE_SYNC_SOURCE,
  WAITER_OPERATIONS_UPDATED_EVENT,
} from '../../services/local/waiterOperations';
import {
  buildPaymentOperationsStorageKey,
  getPaymentOperations,
  PAYMENT_OPERATIONS_REMOTE_SYNC_SOURCE,
  PAYMENT_OPERATIONS_UPDATED_EVENT,
} from '../../services/local/paymentOperations';
import {
  buildOrderNotificationSignature,
  buildServiceNotificationItems,
  countOrderNotificationsByWorkspace,
  isPendingOrderNotificationCandidate,
  playNewOrderChime,
  playWaiterCallChime,
  publishOperationalWorkspaceBadgeCounts,
  showBrowserExternalOrderNotification,
  showBrowserOrderNotification,
} from '../../services/local/serviceNotifications';
import {
  EXTERNAL_ORDERS_UPDATED_EVENT,
  listExternalOrderNotificationCandidates,
} from '../../services/supabase/externalOrdersService';
import {
  readSessionUiState,
  writeSessionUiState,
} from '../../services/local/uiSessionState';
import { setDocumentTitle } from '../../services/local/pageTitle';

const SIDEBAR_EXPANDED = 250;
const SIDEBAR_COLLAPSED = 76;
const MENU_GROUP_PATHS = ['/app/menu', '/app/cocina', '/app/ingredientes'];
const PAYMENTS_GROUP_PATHS = ['/app/pagos', '/app/estadisticas'];
const ADMIN_WATERMARK_LOGO_SLOTS = 20;
const WATERMARK_PATTERN = `url("data:image/svg+xml,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='320' height='320' viewBox='0 0 320 320'><g transform='rotate(-45 160 160)'><g transform='translate(160 104)'><text x='-84' y='10' fill='rgba(15,23,42,0.05)' font-family='Brush Script MT, Segoe Script, cursive' font-style='italic'><tspan font-size='36'>mozzo</tspan><tspan dx='-3' dy='0' font-size='24'>app</tspan></text><line x1='-74' y1='24' x2='72' y2='24' stroke='rgba(15,23,42,0.035)' stroke-width='1.35'/></g><g transform='translate(160 214)'><text x='-77' y='9' fill='rgba(15,23,42,0.038)' font-family='Brush Script MT, Segoe Script, cursive' font-style='italic'><tspan font-size='31'>mozzo</tspan><tspan dx='-3' dy='0' font-size='20'>app</tspan></text><line x1='-67' y1='21' x2='66' y2='21' stroke='rgba(15,23,42,0.028)' stroke-width='1.15'/></g></g></svg>"
)}")`;
const SHARED_OPERATIONAL_SYNC_INTERVAL_MS = 4000;
const EXTERNAL_ORDER_SYNC_INTERVAL_MS = 5000;
const APP_ROUTE_TITLES = [
  ['/app/comercios', 'We!Pedidos Comercios'],
  ['/app/configuracion', 'Configuracion'],
  ['/app/personal', 'Personal'],
  ['/app/estadisticas', 'Estadisticas'],
  ['/app/ventas', 'Ventas'],
  ['/app/pagos/mz', 'Comprar MZ'],
  ['/app/pagos', 'Caja'],
  ['/app/qr', 'QR'],
  ['/app/restaurante', 'Mi Restaurante'],
  ['/app/stock', 'Stock'],
  ['/app/ingredientes', 'Ingredientes'],
  ['/app/promos', 'Promos'],
  ['/app/menu', 'Menu'],
  ['/app/mozos', 'Mozos'],
  ['/app/cocina', 'Cocina'],
];

function LayoutRestaurante() {
  const {
    currentCompany,
    currentBranch,
    currentRestaurant,
    availableBranches,
    accessMode,
    canAccessModule,
    isAdminGeneral,
    isCreatorMode,
    isSupabaseConfigured,
    setActiveBranch,
    resetAccessSelection,
    signOut,
    user,
    canPerformAction,
    currentAccessRole,
  } = useAccessControl();
  const location = useLocation();
  const activeBranchScope = currentBranch || currentRestaurant;
  const internalAssistantEnvironment = getInternalAssistantEnvironment();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [menuHover, setMenuHover] = useState(false);
  const [esMobile, setEsMobile] = useState(window.innerWidth < 768);
  const [isMenuGroupOpen, setIsMenuGroupOpen] = useState(() =>
    MENU_GROUP_PATHS.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`)
    )
  );
  const [isPaymentsGroupOpen, setIsPaymentsGroupOpen] = useState(() =>
    PAYMENTS_GROUP_PATHS.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`)
    )
  );
  const [restaurantProfile, setRestaurantProfile] = useState(() =>
    getRestaurantProfile(activeBranchScope)
  );
  const [branchClosingControl, setBranchClosingControl] = useState(() =>
    getBranchClosingControl(activeBranchScope)
  );
  const [closingClock, setClosingClock] = useState(() => Date.now());
  const [closingPrompt, setClosingPrompt] = useState(null);
  const [showClosingExtensionOptions, setShowClosingExtensionOptions] =
    useState(false);
  const knownNotificationIdsRef = useRef(new Set());
  const notificationHydratedRef = useRef(false);
  const knownExternalOrderIdsRef = useRef(new Set());
  const externalOrdersHydratedRef = useRef(false);
  const externalOrdersSyncingRef = useRef(false);
  const workspaceBadgePartsRef = useRef({
    local: { salon: 0, delivery: 0 },
    externalDelivery: 0,
  });
  const contentScrollRef = useRef(null);
  const previousBranchOpenRef = useRef(null);
  const previousClosingCheckTimeRef = useRef(Date.now());
  const globalServiceStateRef = useRef({
    mesasActivas: [],
    serviceRequests: [],
  });
  const brandTheme = buildRestaurantBrandTheme(restaurantProfile);
  const brandMarkUrl =
    brandTheme.effectiveLogoCompactUrl || brandTheme.effectiveIconLogoUrl || '';
  const restaurantFaviconUrl =
    brandTheme.effectiveIconLogoUrl ||
    brandTheme.effectiveLogoCompactUrl ||
    '';
  const adminWatermarkLogoUrl = brandMarkUrl || '';
  const branchTitle = currentBranch?.name || activeBranchScope?.name || '';
  const salonModuleEnabled =
    restaurantProfile?.serviceMode !== 'kiosk' &&
    restaurantProfile?.salonEnabled !== false;
  const deliveryModuleEnabled = restaurantProfile?.deliveryEnabled === true;
  const deliveryOnlyExperience = isDeliveryOnly(restaurantProfile);
  const waiterModuleEnabled =
    salonModuleEnabled && restaurantProfile?.waiterEnabled !== false;
  const stockModuleEnabled = restaurantProfile?.stockEnabled !== false;
  const canManageBranchClosing = canPerformAction('manage_settings');
  const branchOperatingState = getEffectiveBranchOperatingState(
    restaurantProfile?.horarioDetallado,
    new Date(closingClock),
    branchClosingControl
  );
  const publishWorkspaceBadgeCounts = useCallback(
    (updates = {}) => {
      const previous = workspaceBadgePartsRef.current || {
        local: { salon: 0, delivery: 0 },
        externalDelivery: 0,
      };
      const nextLocal = updates.local
        ? {
            salon: Math.max(0, Number(updates.local.salon || 0)),
            delivery: Math.max(0, Number(updates.local.delivery || 0)),
          }
        : previous.local;
      const nextExternalDelivery = Object.prototype.hasOwnProperty.call(
        updates,
        'externalDelivery'
      )
        ? Math.max(0, Number(updates.externalDelivery || 0))
        : previous.externalDelivery;

      workspaceBadgePartsRef.current = {
        local: nextLocal,
        externalDelivery: nextExternalDelivery,
      };
      publishOperationalWorkspaceBadgeCounts(activeBranchScope?.id, {
        salon: nextLocal.salon,
        delivery: nextLocal.delivery + nextExternalDelivery,
      });
    },
    [activeBranchScope?.id]
  );
  const getPendingOrderSignatureMap = useCallback((mesasActivas = []) => {
    const signatureMap = new Map();

    (Array.isArray(mesasActivas) ? mesasActivas : []).forEach((mesa) => {
      if (!isPendingOrderNotificationCandidate(mesa) || !mesa?.id) {
        return;
      }

      signatureMap.set(mesa.id, buildOrderNotificationSignature(mesa));
    });

    return signatureMap;
  }, []);
  const syncGlobalServiceState = useCallback(({ reset = false, allowNotify = false } = {}) => {
    const previousState = globalServiceStateRef.current;
    const operationalState = getOperationalOrdersState(activeBranchScope, () => ({
      mesasActivas: [],
      pedidosHistoricos: [],
    }));
    const waiterState = getWaiterOperationsState(activeBranchScope);
    const nextState = {
      mesasActivas: operationalState.mesasActivas || [],
      serviceRequests: getPendingWaiterServiceRequests(
        waiterState,
        currentBranch?.id || activeBranchScope?.id || ''
      ),
    };
    const nextItems = buildServiceNotificationItems(
      nextState.mesasActivas,
      nextState.serviceRequests,
      currentBranch?.id
    );
    publishWorkspaceBadgeCounts({
      local: countOrderNotificationsByWorkspace(
        nextState.mesasActivas.filter(isPendingOrderNotificationCandidate)
      ),
    });
    const currentIds = new Set(nextItems.map((item) => item.id));

    if (reset || !notificationHydratedRef.current) {
      knownNotificationIdsRef.current = currentIds;
      notificationHydratedRef.current = true;
    } else if (allowNotify) {
      const newNotifications = nextItems.filter(
        (item) => !knownNotificationIdsRef.current.has(item.id)
      );
      const previousOrderSignatures = getPendingOrderSignatureMap(previousState.mesasActivas);
      const updatedOrderNotifications = nextItems.filter((item) => {
        if (item.kind !== 'order' || !item.raw?.id) {
          return false;
        }

        if (!previousOrderSignatures.has(item.raw.id)) {
          return false;
        }

        return (
          previousOrderSignatures.get(item.raw.id) !==
          buildOrderNotificationSignature(item.raw)
        );
      });

      if (newNotifications.length > 0 || updatedOrderNotifications.length > 0) {
        const hasNewOrder = newNotifications.some((item) => item.kind === 'order');
        const hasNewWaiterCall = newNotifications.some(
          (item) => item.kind === 'waiter-request'
        );

        if (hasNewOrder || updatedOrderNotifications.length > 0) {
          playNewOrderChime();
        } else if (
          hasNewWaiterCall &&
          !document.querySelector('.inicio-salon-modal')
        ) {
          playWaiterCallChime();
        }

        const firstOrderNotification = newNotifications.find((item) => item.kind === 'order');
        const firstUpdatedOrderNotification = updatedOrderNotifications[0];

        if (firstOrderNotification?.raw) {
          showBrowserOrderNotification(firstOrderNotification.raw, newNotifications.length - 1);
        } else if (firstUpdatedOrderNotification?.raw) {
          showBrowserOrderNotification(
            firstUpdatedOrderNotification.raw,
            Math.max(0, updatedOrderNotifications.length - 1),
            { updated: true }
          );
        }
      }

      knownNotificationIdsRef.current = currentIds;
    }

    globalServiceStateRef.current = nextState;
  }, [activeBranchScope, currentBranch?.id, getPendingOrderSignatureMap, publishWorkspaceBadgeCounts]);

  const syncSharedOperationalState = useCallback(async () => {
    if (!activeBranchScope?.id) {
      return;
    }

    try {
      const result = await hydrateSharedOperationalStateToLocal(activeBranchScope, () => ({
        mesasActivas: [],
        pedidosHistoricos: [],
      }));

      if (result?.supported && result.availableSections?.closingControl) {
        const localControl = getBranchClosingControl(activeBranchScope);
        const newestControl = selectNewestBranchClosingControl(
          localControl,
          result.payload?.closingControl
        );

        if (newestControl.updatedAt !== localControl.updatedAt) {
          saveBranchClosingControl(activeBranchScope, newestControl, {
            source: BRANCH_CLOSING_CONTROL_REMOTE_SYNC_SOURCE,
          });
        }

        setBranchClosingControl(newestControl);
      }
    } catch (error) {
      console.error('No pudimos sincronizar el estado operativo compartido:', error);
    }
  }, [activeBranchScope]);

  const syncExternalOrderNotifications = useCallback(async ({ reset = false } = {}) => {
    const branchId = activeBranchScope?.id;

    if (
      !branchId ||
      isCreatorMode ||
      !isSupabaseConfigured ||
      !canAccessModule('pedidos') ||
      externalOrdersSyncingRef.current
    ) {
      return;
    }

    externalOrdersSyncingRef.current = true;

    try {
      const nextOrders = await listExternalOrderNotificationCandidates(branchId);
      publishWorkspaceBadgeCounts({ externalDelivery: nextOrders.length });
      const nextIds = new Set(nextOrders.map((order) => order.id).filter(Boolean));

      if (reset || !externalOrdersHydratedRef.current) {
        knownExternalOrderIdsRef.current = nextIds;
        externalOrdersHydratedRef.current = true;
        return;
      }

      const newOrders = nextOrders.filter(
        (order) => order?.id && !knownExternalOrderIdsRef.current.has(order.id)
      );

      knownExternalOrderIdsRef.current = nextIds;

      if (newOrders.length === 0) {
        return;
      }

      playNewOrderChime();
      showBrowserExternalOrderNotification(newOrders[0], newOrders.length - 1);
      window.dispatchEvent(
        new CustomEvent(EXTERNAL_ORDERS_UPDATED_EVENT, {
          detail: {
            branchId,
            newOrderIds: newOrders.map((order) => order.id),
          },
        })
      );
    } catch (error) {
      console.warn('No pudimos revisar pedidos nuevos de Delivery:', error);
    } finally {
      externalOrdersSyncingRef.current = false;
    }
  }, [
    activeBranchScope?.id,
    canAccessModule,
    isCreatorMode,
    isSupabaseConfigured,
    publishWorkspaceBadgeCounts,
  ]);

  const toggleMenu = () => setMenuAbierto(!menuAbierto);

  useEffect(() => {
    const manejarResize = () => {
      const mobile = window.innerWidth < 768;
      setEsMobile(mobile);
      setMenuAbierto(false);
      setMenuHover(false);
    };

    window.addEventListener('resize', manejarResize);
    manejarResize();
    return () => window.removeEventListener('resize', manejarResize);
  }, []);

  useEffect(() => {
    setRestaurantProfile(getRestaurantProfile(activeBranchScope));

    const handleProfileUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || 'default';
      const currentRestaurantId = activeBranchScope?.id || 'default';

      if (updatedRestaurantId !== currentRestaurantId) {
        return;
      }

      setRestaurantProfile(event.detail?.profile || getRestaurantProfile(activeBranchScope));
    };

    const handleStorage = (event) => {
      const expectedKey = buildRestaurantProfileStorageKey(activeBranchScope?.id);
      if (event.key !== expectedKey) {
        return;
      }

      setRestaurantProfile(getRestaurantProfile(activeBranchScope));
    };

    window.addEventListener(RESTAURANT_PROFILE_UPDATED_EVENT, handleProfileUpdate);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(RESTAURANT_PROFILE_UPDATED_EVENT, handleProfileUpdate);
      window.removeEventListener('storage', handleStorage);
    };
  }, [activeBranchScope]);

  useEffect(() => {
    setBranchClosingControl(getBranchClosingControl(activeBranchScope));
    setClosingPrompt(null);
    setShowClosingExtensionOptions(false);
    previousBranchOpenRef.current = null;
    previousClosingCheckTimeRef.current = Date.now();

    const currentBranchId = activeBranchScope?.id || 'default';
    const handleClosingControlUpdate = (event) => {
      if ((event.detail?.branchId || 'default') !== currentBranchId) {
        return;
      }

      const nextControl = selectNewestBranchClosingControl(
        getBranchClosingControl(activeBranchScope),
        event.detail?.payload
      );
      setBranchClosingControl(nextControl);

      if (
        event.detail?.source === BRANCH_CLOSING_CONTROL_REMOTE_SYNC_SOURCE ||
        isCreatorMode ||
        !isSupabaseConfigured ||
        !activeBranchScope?.id
      ) {
        return;
      }

      void pushSharedBranchClosingControl(activeBranchScope, nextControl).catch(
        (error) => {
          console.error(
            'No pudimos sincronizar la decision de cierre de la sucursal:',
            error
          );
        }
      );
    };
    const handleClosingStorage = (event) => {
      if (
        event.key !==
        buildBranchClosingControlStorageKey(activeBranchScope?.id)
      ) {
        return;
      }

      setBranchClosingControl(getBranchClosingControl(activeBranchScope));
    };

    window.addEventListener(
      BRANCH_CLOSING_CONTROL_UPDATED_EVENT,
      handleClosingControlUpdate
    );
    window.addEventListener('storage', handleClosingStorage);

    return () => {
      window.removeEventListener(
        BRANCH_CLOSING_CONTROL_UPDATED_EVENT,
        handleClosingControlUpdate
      );
      window.removeEventListener('storage', handleClosingStorage);
    };
  }, [activeBranchScope, isCreatorMode, isSupabaseConfigured]);

  useEffect(() => {
    const refreshClosingClock = () => setClosingClock(Date.now());
    const intervalId = window.setInterval(refreshClosingClock, 5000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshClosingClock();
      }
    };

    window.addEventListener('focus', refreshClosingClock);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshClosingClock);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const previousOpen = previousBranchOpenRef.current;
    const previousCheckedAt = previousClosingCheckTimeRef.current;
    const nowTimestamp = Number(closingClock || Date.now());

    previousBranchOpenRef.current = branchOperatingState.isOpen;
    previousClosingCheckTimeRef.current = nowTimestamp;

    if (!activeBranchScope?.id || !canManageBranchClosing) {
      return;
    }

    if (branchOperatingState.isOpen) {
      setClosingPrompt(null);
      setShowClosingExtensionOptions(false);
      return;
    }

    const boundary = getMostRecentBranchClosingBoundary(
      restaurantProfile?.horarioDetallado,
      new Date(nowTimestamp),
      branchClosingControl
    );

    if (!boundary?.boundaryAt) {
      return;
    }

    if (
      isBranchClosingBoundaryHandled(
        branchClosingControl,
        boundary.boundaryAt
      )
    ) {
      setClosingPrompt((currentPrompt) =>
        currentPrompt?.boundaryAt === boundary.boundaryAt
          ? null
          : currentPrompt
      );
      setShowClosingExtensionOptions(false);
      return;
    }

    const boundaryTimestamp = new Date(boundary.boundaryAt).getTime();
    const crossedBoundaryWhileOpen =
      previousOpen === true &&
      boundaryTimestamp > previousCheckedAt &&
      boundaryTimestamp <= nowTimestamp;
    const isRecentBoundaryOnFirstLoad =
      previousOpen === null &&
      nowTimestamp >= boundaryTimestamp &&
      nowTimestamp - boundaryTimestamp <=
        BRANCH_CLOSING_INITIAL_PROMPT_GRACE_MS;

    if (!crossedBoundaryWhileOpen && !isRecentBoundaryOnFirstLoad) {
      return;
    }

    setShowClosingExtensionOptions(false);
    setClosingPrompt((currentPrompt) => {
      if (currentPrompt?.boundaryAt === boundary.boundaryAt) {
        return currentPrompt;
      }

      return boundary;
    });
  }, [
    activeBranchScope?.id,
    branchClosingControl,
    branchOperatingState.isOpen,
    canManageBranchClosing,
    closingClock,
    restaurantProfile?.horarioDetallado,
  ]);

  const handleBranchClosingDecision = useCallback(
    (decision, requestedMinutes = 0) => {
      if (!closingPrompt?.boundaryAt || !canManageBranchClosing) {
        return;
      }

      const extensionMinutes = BRANCH_CLOSING_EXTENSION_OPTIONS.includes(
        Number(requestedMinutes)
      )
        ? Number(requestedMinutes)
        : 0;
      const shouldExtend = decision === 'extend' && extensionMinutes > 0;
      const nowDate = new Date();
      const nextControl = saveBranchClosingControl(activeBranchScope, {
        extensionUntil: shouldExtend
          ? new Date(nowDate.getTime() + extensionMinutes * 60 * 1000).toISOString()
          : '',
        scheduledCloseAt:
          closingPrompt.kind === 'scheduled'
            ? closingPrompt.boundaryAt
            : branchClosingControl.scheduledCloseAt || closingPrompt.boundaryAt,
        handledBoundaryAt: closingPrompt.boundaryAt,
        decision: shouldExtend ? 'extend' : 'close',
        extensionMinutes: shouldExtend ? extensionMinutes : 0,
        updatedAt: nowDate.toISOString(),
        updatedBy: user?.id || user?.email || '',
      });

      setBranchClosingControl(nextControl);
      setClosingPrompt(null);
      setShowClosingExtensionOptions(false);
      setClosingClock(nowDate.getTime());
    },
    [
      activeBranchScope,
      branchClosingControl.scheduledCloseAt,
      canManageBranchClosing,
      closingPrompt,
      user?.email,
      user?.id,
    ]
  );

  useEffect(() => {
    let active = true;

    const bootstrapRemoteBranchState = async () => {
      if (!activeBranchScope?.id || isCreatorMode || !isSupabaseConfigured) {
        return;
      }

      try {
        const report = await hydrateCriticalBranchStateToLocal(activeBranchScope, {
          localProfile: getRestaurantProfile(activeBranchScope),
        });

        if (!active) {
          return;
        }

        if (report.updatedProfile) {
          setRestaurantProfile(report.updatedProfile);
        }

        if (report.issues.length > 0) {
          report.issues.forEach((issue) => {
            console.warn(
              `No pudimos terminar la sincronizacion remota de ${issue.scope}:`,
              issue.message
            );
          });
        }
      } catch (error) {
        if (!active) {
          return;
        }

        console.error('No pudimos bootstrapear el estado remoto critico de la sucursal:', error);
      }
    };

    bootstrapRemoteBranchState();

    return () => {
      active = false;
    };
  }, [activeBranchScope, isCreatorMode, isSupabaseConfigured]);

  useEffect(() => {
    const matchedRouteTitle = APP_ROUTE_TITLES.find(
      ([path]) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`)
    )?.[1];
    const pageTitle =
      location.pathname === '/app'
        ? 'Central'
        : matchedRouteTitle || 'Central';

    setDocumentTitle(pageTitle, branchTitle);
  }, [branchTitle, location.pathname]);

  useEffect(() => {
    if (restaurantFaviconUrl) {
      applyHeadBrandingAssets(restaurantFaviconUrl);
      return;
    }

    applyInstitutionalBrandingAssets();
  }, [location.pathname, restaurantFaviconUrl]);

  useEffect(() => {
    knownNotificationIdsRef.current = new Set();
    notificationHydratedRef.current = false;
    workspaceBadgePartsRef.current = {
      local: { salon: 0, delivery: 0 },
      externalDelivery: 0,
    };
    globalServiceStateRef.current = {
      mesasActivas: [],
      serviceRequests: [],
    };
    publishWorkspaceBadgeCounts({
      local: { salon: 0, delivery: 0 },
      externalDelivery: 0,
    });
    syncGlobalServiceState({ reset: true });
  }, [
    activeBranchScope?.id,
    currentBranch?.id,
    publishWorkspaceBadgeCounts,
    syncGlobalServiceState,
  ]);

  useEffect(() => {
    knownExternalOrderIdsRef.current = new Set();
    externalOrdersHydratedRef.current = false;
    externalOrdersSyncingRef.current = false;

    if (
      !activeBranchScope?.id ||
      isCreatorMode ||
      !isSupabaseConfigured ||
      !canAccessModule('pedidos')
    ) {
      publishWorkspaceBadgeCounts({ externalDelivery: 0 });
      return undefined;
    }

    void syncExternalOrderNotifications({ reset: true });
    const intervalId = window.setInterval(
      syncExternalOrderNotifications,
      EXTERNAL_ORDER_SYNC_INTERVAL_MS
    );
    const handleFocus = () => {
      void syncExternalOrderNotifications();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [
    activeBranchScope?.id,
    canAccessModule,
    isCreatorMode,
    isSupabaseConfigured,
    publishWorkspaceBadgeCounts,
    syncExternalOrderNotifications,
  ]);

  useEffect(() => {
    if (!activeBranchScope?.id) {
      return undefined;
    }

    syncSharedOperationalState();

    const intervalId = window.setInterval(
      syncSharedOperationalState,
      SHARED_OPERATIONAL_SYNC_INTERVAL_MS
    );
    const handleFocus = () => {
      syncSharedOperationalState();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [activeBranchScope?.id, syncSharedOperationalState]);

  useEffect(() => {
    const currentRestaurantId = activeBranchScope?.id || 'default';

    const handleWaiterSync = (event) => {
      if ((event.detail?.restaurantId || 'default') !== currentRestaurantId) {
        return;
      }

      if (event.detail?.source === WAITER_OPERATIONS_REMOTE_SYNC_SOURCE) {
        return;
      }

      const nextWaiterOps =
        event.detail?.payload || getWaiterOperationsState(activeBranchScope);

      void pushSharedWaiterOperationsState(activeBranchScope, nextWaiterOps).catch((error) => {
        console.error('No pudimos sincronizar la operacion compartida de mozos:', error);
      });
    };

    const handlePaymentSync = (event) => {
      if ((event.detail?.restaurantId || 'default') !== currentRestaurantId) {
        return;
      }

      if (event.detail?.source === PAYMENT_OPERATIONS_REMOTE_SYNC_SOURCE) {
        return;
      }

      const nextPaymentOperations =
        event.detail?.operations || getPaymentOperations(activeBranchScope);

      void pushSharedPaymentOperationsState(
        activeBranchScope,
        nextPaymentOperations
      ).catch((error) => {
        console.error('No pudimos sincronizar los pagos compartidos:', error);
      });
    };

    window.addEventListener(WAITER_OPERATIONS_UPDATED_EVENT, handleWaiterSync);
    window.addEventListener(PAYMENT_OPERATIONS_UPDATED_EVENT, handlePaymentSync);

    return () => {
      window.removeEventListener(WAITER_OPERATIONS_UPDATED_EVENT, handleWaiterSync);
      window.removeEventListener(PAYMENT_OPERATIONS_UPDATED_EVENT, handlePaymentSync);
    };
  }, [activeBranchScope]);

  useEffect(() => {
    const currentRestaurantId = activeBranchScope?.id || 'default';

    const handleOperationalUpdate = (event) => {
      if ((event.detail?.restaurantId || 'default') !== currentRestaurantId) {
        return;
      }

      const previousOrderSignatures = getPendingOrderSignatureMap(
        globalServiceStateRef.current.mesasActivas
      );
      const nextMesasActivas = event.detail?.payload?.mesasActivas || [];
      const newNotificationIds = event.detail?.newNotificationIds || [];
      const updatedOrders = nextMesasActivas.filter((mesa) => {
        if (!isPendingOrderNotificationCandidate(mesa) || !mesa?.id) {
          return false;
        }

        if (newNotificationIds.includes(mesa.id) || !previousOrderSignatures.has(mesa.id)) {
          return false;
        }

        return (
          previousOrderSignatures.get(mesa.id) !== buildOrderNotificationSignature(mesa)
        );
      });

      if (newNotificationIds.length > 0 || updatedOrders.length > 0) {
        playNewOrderChime();
        const firstOrder = nextMesasActivas.find((mesa) =>
          newNotificationIds.includes(mesa.id)
        );

        if (firstOrder) {
          showBrowserOrderNotification(
            firstOrder,
            Math.max(0, Number(newNotificationIds.length) - 1)
          );
        } else if (updatedOrders[0]) {
          showBrowserOrderNotification(
            updatedOrders[0],
            Math.max(0, updatedOrders.length - 1),
            { updated: true }
          );
        }
      }

      syncGlobalServiceState();
    };

    const handleWaiterUpdate = (event) => {
      if ((event.detail?.restaurantId || 'default') !== currentRestaurantId) {
        return;
      }

      if (
        (event.detail?.newServiceRequestIds || []).length > 0 &&
        !document.querySelector('.inicio-salon-modal')
      ) {
        playWaiterCallChime();
      }

      syncGlobalServiceState();
    };

    const handleStorage = (event) => {
      const operationalKey = buildOperationalOrdersStorageKey(activeBranchScope?.id);
      const waiterKey = buildWaiterOperationsStorageKey(activeBranchScope?.id);
      const paymentKey = buildPaymentOperationsStorageKey(activeBranchScope?.id);

      if (event.key !== operationalKey && event.key !== waiterKey && event.key !== paymentKey) {
        return;
      }

      syncGlobalServiceState({ allowNotify: true });
    };

    window.addEventListener(OPERATIONAL_ORDERS_UPDATED_EVENT, handleOperationalUpdate);
    window.addEventListener(WAITER_OPERATIONS_UPDATED_EVENT, handleWaiterUpdate);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(
        OPERATIONAL_ORDERS_UPDATED_EVENT,
        handleOperationalUpdate
      );
      window.removeEventListener(WAITER_OPERATIONS_UPDATED_EVENT, handleWaiterUpdate);
      window.removeEventListener('storage', handleStorage);
    };
  }, [activeBranchScope, getPendingOrderSignatureMap, syncGlobalServiceState]);

  const sidebarWidth = esMobile
    ? SIDEBAR_EXPANDED
    : menuHover
      ? SIDEBAR_EXPANDED
      : SIDEBAR_COLLAPSED;
  const routeScrollStateKey = `mozzoapp:route-scroll:${
    activeBranchScope?.id || 'default'
  }:${location.pathname}`;

  const mostrarTextoMenu = esMobile || menuHover;
  const menuCompacto = !esMobile && !menuHover;
  const isInicio = location.pathname === '/app';
  const isMiRestaurante = location.pathname === '/app/restaurante';
  const isConfiguracion = location.pathname === '/app/configuracion';
  const heroUploadMode = isMiRestaurante || isConfiguracion
    ? 'always'
    : isInicio
      ? 'if-empty'
      : 'never';

  useEffect(() => {
    const isInsideMenuGroup = MENU_GROUP_PATHS.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
    const isInsidePaymentsGroup = PAYMENTS_GROUP_PATHS.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`)
    );

    if (isInsideMenuGroup) {
      setIsMenuGroupOpen(true);
    }

    if (isInsidePaymentsGroup) {
      setIsPaymentsGroupOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const contentElement = contentScrollRef.current;

    if (!contentElement) {
      return undefined;
    }

    const restoreScroll = () => {
      const savedState = readSessionUiState(routeScrollStateKey, null);
      const nextScrollTop = Math.max(0, Number(savedState?.scrollTop || 0));
      contentElement.scrollTo({ top: nextScrollTop, left: 0, behavior: 'auto' });
    };

    const frameId = window.requestAnimationFrame(() => {
      restoreScroll();
      window.requestAnimationFrame(restoreScroll);
    });
    const timeoutId = window.setTimeout(restoreScroll, 120);
    const saveScrollPosition = () => {
      writeSessionUiState(routeScrollStateKey, {
        scrollTop: contentElement.scrollTop,
      });
    };
    let saveFrameId = 0;
    const scheduleSaveScrollPosition = () => {
      if (saveFrameId) {
        return;
      }

      saveFrameId = window.requestAnimationFrame(() => {
        saveFrameId = 0;
        saveScrollPosition();
      });
    };

    contentElement.addEventListener('scroll', scheduleSaveScrollPosition, {
      passive: true,
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      if (saveFrameId) {
        window.cancelAnimationFrame(saveFrameId);
      }
      window.clearTimeout(timeoutId);
      saveScrollPosition();
      contentElement.removeEventListener('scroll', scheduleSaveScrollPosition);
    };
  }, [routeScrollStateKey]);

  const matchesRoute = (to, exact = false) =>
    Boolean(
      to &&
        (location.pathname === to ||
          (!exact && to !== "/app" && location.pathname.startsWith(`${to}/`)))
    );

  const navigationItems = [
    {
      key: 'inicio',
      IconComponent: InicioIcon,
      text: 'Inicio',
      to: deliveryOnlyExperience ? '/app/comercios' : '/app',
      visible: canAccessModule('inicio'),
    },
    {
      key: 'comercios',
      IconComponent: PedidosIcon,
      text: 'Mi comercio',
      to: '/app/comercios',
      visible: deliveryModuleEnabled && !deliveryOnlyExperience && canAccessModule('inicio'),
    },
    {
      key: 'menu-group',
      groupKey: 'menu',
      IconComponent: MenuIcon,
      text: 'Menu',
      children: [
        {
          key: 'menu',
          IconComponent: MenuIcon,
          text: deliveryOnlyExperience ? 'Mi menu' : 'Carta',
          to: '/app/menu',
          visible: canAccessModule('menu'),
        },
        {
          key: 'cocina',
          IconComponent: CocinaIcon,
          text: 'Cocina',
          to: '/app/cocina',
          visible: canAccessModule('cocina'),
        },
        {
          key: 'ingredientes',
          IconComponent: IngredientesIcon,
          text: 'Ingredientes',
          to: '/app/ingredientes',
          visible: (!deliveryOnlyExperience || stockModuleEnabled) && canAccessModule('ingredientes'),
        },
      ],
      visible:
        canAccessModule('menu') ||
        canAccessModule('cocina') ||
        ((!deliveryOnlyExperience || stockModuleEnabled) && canAccessModule('ingredientes')),
    },
    {
      key: 'promos',
      IconComponent: PromosIcon,
      text: 'Promos',
      to: '/app/promos',
      visible: canAccessModule('promos'),
    },
    {
      key: 'pedidos',
      IconComponent: PedidosIcon,
      text: 'Delivery',
      to: '/app/pedidos',
      visible: deliveryModuleEnabled && canAccessModule('pedidos'),
    },
    {
      key: 'mozos',
      IconComponent: MozoIcon,
      text: 'Mozo',
      to: '/app/mozos',
      visible: waiterModuleEnabled && canAccessModule('mozos'),
    },
    {
      key: 'stock',
      IconComponent: StockIcon,
      text: 'Stock',
      to: '/app/stock',
      visible: stockModuleEnabled && canAccessModule('stock'),
    },
    {
      key: 'restaurante',
      IconComponent: MiRestauranteIcon,
      text: deliveryOnlyExperience ? 'Mi comercio' : 'Mi Restaurante',
      to: '/app/restaurante',
      visible: canAccessModule('restaurante'),
    },
    {
      key: 'qr',
      IconComponent: QrMezaIcon,
      text: 'Qr Por Mesa',
      to: '/app/qr',
      visible: salonModuleEnabled && canAccessModule('qr'),
    },
    {
      key: 'ventas',
      IconComponent: EstadisticasIcon,
      text: 'Ventas',
      to: '/app/ventas',
      visible: canAccessModule('pagos'),
    },
    {
      key: 'pagos-group',
      groupKey: 'payments',
      IconComponent: PagosIcon,
      text: 'Caja',
      children: [
        {
          key: 'pagos',
          IconComponent: PagosIcon,
          text: 'Caja',
          to: '/app/pagos',
          exact: true,
          visible: canAccessModule('pagos'),
        },
        {
          key: 'comprar-mz',
          IconComponent: MzCreditsIcon,
          text: 'Comprar MZ',
          to: '/app/pagos/mz',
          exact: true,
          visible: canAccessModule('pagos'),
        },
        {
          key: 'estadisticas',
          IconComponent: EstadisticasIcon,
          text: 'Estadisticas',
          to: '/app/estadisticas',
          visible: canAccessModule('estadisticas'),
        },
      ],
      visible: canAccessModule('pagos') || canAccessModule('estadisticas'),
    },
    {
      key: 'configuracion',
      IconComponent: ConfiguracionIcon,
      text: 'Configuracion',
      to: '/app/configuracion',
      visible: canAccessModule('configuracion'),
    },
    {
      key: 'personal',
      IconComponent: PersonalIcon,
      text: 'Personal',
      to: '/app/personal',
      visible: canAccessModule('personal'),
    },
    {
      key: 'backoffice-local',
      IconComponent: ConfiguracionIcon,
      text: 'Backoffice local',
      to: '/backoffice',
      visible: isLocalRuntimeHost(),
    },
  ].filter((item) => item.visible !== false);

  const renderNavigationGroup = (item) => {
    const visibleChildren = (item.children || []).filter((child) => child.visible !== false);

    if (!visibleChildren.length) {
      return null;
    }

    const isGroupActive = visibleChildren.some((child) =>
      matchesRoute(child.to, child.exact)
    );
    const isGroupOpen =
      item.groupKey === 'payments' ? isPaymentsGroupOpen : isMenuGroupOpen;
    const toggleGroup = () => {
      if (item.groupKey === 'payments') {
        setIsPaymentsGroupOpen((previousValue) => !previousValue);
        return;
      }

      setIsMenuGroupOpen((previousValue) => !previousValue);
    };

    return (
      <div key={item.key}>
        <button
          type="button"
          onClick={toggleGroup}
          style={{
            width: '100%',
            border: 'none',
            background: isGroupActive ? 'var(--brand-soft, #eff6ff)' : 'transparent',
            color: isGroupActive ? 'var(--brand-primary, #2563eb)' : '#333',
            cursor: 'pointer',
            padding: 0,
            textAlign: 'left',
            borderRight: isGroupActive
              ? '3px solid var(--brand-primary, #2563eb)'
              : '3px solid transparent',
          }}
          title={item.text}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: menuCompacto ? 'center' : 'space-between',
              gap: mostrarTextoMenu ? '10px' : '0px',
              padding: menuCompacto ? '12px 10px' : '10px 20px',
              transition: 'background-color 0.2s ease, color 0.2s ease',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: menuCompacto ? 'center' : 'flex-start',
                gap: mostrarTextoMenu ? '10px' : '0px',
                minWidth: 0,
              }}
            >
              {item.IconComponent && <item.IconComponent />}
              {mostrarTextoMenu && (
                <span
                  style={{
                    fontSize: '16px',
                    fontWeight: isGroupActive ? 700 : 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.text}
                </span>
              )}
            </div>

            {mostrarTextoMenu && (
              <svg
                viewBox="0 0 16 16"
                style={{
                  width: '14px',
                  height: '14px',
                  flexShrink: 0,
                  transform: isGroupOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              >
                <path
                  d="M5 3.5 10 8l-5 4.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </button>

        {mostrarTextoMenu && isGroupOpen && (
          <div
            style={{
              display: 'grid',
              gap: '2px',
              paddingBottom: '6px',
            }}
          >
            {visibleChildren.map((child) => (
              <MenuItem
                key={child.key}
                IconComponent={child.IconComponent}
                text={child.text}
                to={child.to}
                exact={child.exact}
                showText
                compact={false}
                style={{
                  padding: '9px 20px 9px 46px',
                  backgroundColor: matchesRoute(child.to, child.exact)
                    ? 'rgba(255,255,255,0.7)'
                    : 'transparent',
                }}
                textStyle={{
                  fontSize: '14px',
                  fontWeight: matchesRoute(child.to, child.exact) ? 700 : 500,
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <IngredientesProvider>
      <div
        style={{
          display: 'flex',
          height: '100vh',
          width: '100vw',
          overflow: 'hidden',
          position: 'relative',
          fontFamily: brandTheme.fontFamily,
          '--brand-primary': brandTheme.primaryColor,
          '--brand-secondary': brandTheme.secondaryColor,
          '--brand-soft': brandTheme.softColor,
          '--brand-page': brandTheme.pageTint,
          '--brand-border': brandTheme.borderColor,
          '--brand-hero': brandTheme.heroBackground,
        }}
      >
        {esMobile && (
          <button
            onClick={toggleMenu}
            style={{
              position: 'fixed',
              top: '10px',
              left: '10px',
              zIndex: 2000,
              padding: '10px',
              background: '#fff',
              border: '1px solid #ccc',
              borderRadius: '5px',
              cursor: 'pointer',
            }}
          >
            Menu
          </button>
        )}

        <div
          onMouseEnter={() => !esMobile && setMenuHover(true)}
          onMouseLeave={() => !esMobile && setMenuHover(false)}
          style={{
            width: `${sidebarWidth}px`,
            backgroundColor: 'white',
            boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
            position: 'fixed',
            height: '100vh',
            top: 0,
            left: esMobile ? (menuAbierto ? 0 : `-${SIDEBAR_EXPANDED}px`) : 0,
            transition: 'left 0.3s ease, width 0.25s ease',
            zIndex: 1000,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <Card>
            <div
              style={{
                marginBottom: '20px',
                padding: mostrarTextoMenu ? '4px 20px 0' : '4px 10px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: mostrarTextoMenu ? 'flex-start' : 'center',
                gap: '12px',
              }}
            >
              {brandMarkUrl ? (
                <div
                  style={{
                    width: mostrarTextoMenu ? '56px' : '40px',
                    height: mostrarTextoMenu ? '42px' : '34px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={brandMarkUrl}
                    alt="Logo del restaurante"
                    style={{
                      width: 'auto',
                      height: 'auto',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    width: mostrarTextoMenu ? '42px' : '34px',
                    height: mostrarTextoMenu ? '42px' : '34px',
                    borderRadius: '12px',
                    backgroundColor: 'var(--brand-soft)',
                    border: '1px solid var(--brand-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--brand-primary)',
                    fontWeight: 800,
                    fontSize: mostrarTextoMenu ? '16px' : '13px',
                    flexShrink: 0,
                  }}
                >
                  {(restaurantProfile?.nombre || activeBranchScope?.name || 'M').slice(0, 1)}
                </div>
              )}

              {mostrarTextoMenu && (
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      color: '#0f172a',
                      fontWeight: 800,
                      fontSize: '15px',
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                    }}
                  >
                    {restaurantProfile?.nombre || activeBranchScope?.name || 'MozzoApp'}
                  </div>
                  <div
                    style={{
                      marginTop: '4px',
                      color: 'var(--brand-primary)',
                      fontSize: '11px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Identidad activa
                  </div>
                </div>
              )}
            </div>

            {mostrarTextoMenu && availableBranches.length > 1 && accessMode === 'global' && (
              <div
                style={{
                  padding: '0 20px 18px',
                  display: 'grid',
                  gap: '8px',
                }}
              >
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>
                  Sucursal activa
                </div>
                <select
                  value={currentBranch?.id || ''}
                  onChange={(event) => setActiveBranch(event.target.value)}
                  style={{
                    width: '100%',
                    borderRadius: 10,
                    border: '1px solid #cbd5e1',
                    padding: '10px 12px',
                    backgroundColor: '#ffffff',
                    color: '#0f172a',
                    fontSize: 14,
                  }}
                >
                  {availableBranches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {mostrarTextoMenu && !isAdminGeneral && currentBranch?.name && (
              <div
                style={{
                  margin: '0 20px 18px',
                  padding: '10px 12px',
                  borderRadius: 12,
                  backgroundColor: brandTheme.softColor,
                  color: brandTheme.primaryColor,
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                Sesion en sucursal: {currentBranch.name}
              </div>
            )}

            {mostrarTextoMenu && isAdminGeneral && accessMode === 'branch' && currentBranch?.name && (
              <div
                style={{
                  margin: '0 20px 18px',
                  padding: '10px 12px',
                  borderRadius: 12,
                  backgroundColor: '#fff7ed',
                  color: '#c2410c',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                Trabajando en sucursal: {currentBranch.name}
              </div>
            )}

            {navigationItems.map((item) =>
              item.children ? (
                renderNavigationGroup(item)
              ) : (
                <MenuItem
                  key={item.key}
                  IconComponent={item.IconComponent}
                  text={item.text}
                  to={item.to}
                  showText={mostrarTextoMenu}
                  compact={menuCompacto}
                />
              )
            )}

            <div
              style={{
                marginTop: '18px',
                padding: mostrarTextoMenu ? '12px 20px 4px' : '12px 10px 4px',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              {mostrarTextoMenu && (
                <>
                  <div style={{ marginBottom: '4px', fontSize: 12, color: '#64748b' }}>
                    {currentCompany?.name || 'Empresa activa'}
                  </div>
                  <div style={{ marginBottom: '10px', fontSize: 12, color: '#94a3b8' }}>
                    {accessMode === 'global'
                      ? `Panel general${currentBranch?.name ? ` · ${currentBranch.name}` : ''}`
                      : currentBranch?.name || user?.email || 'Sesion activa'}
                  </div>
                </>
              )}

              {!esMobile && mostrarTextoMenu && !isCreatorMode && (
                <button
                  type="button"
                  onClick={resetAccessSelection}
                  style={{
                    width: '100%',
                    marginBottom: '10px',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1px solid ${brandTheme.borderColor}`,
                    backgroundColor: brandTheme.softColor,
                    color: brandTheme.primaryColor,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cambiar acceso
                </button>
              )}

              <button
                type="button"
                onClick={signOut}
                style={{
                  width: '100%',
                  padding: menuCompacto ? '10px 8px' : '10px 12px',
                  borderRadius: 10,
                  border: '1px solid #fecaca',
                  backgroundColor: '#fff1f2',
                  color: '#be123c',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                title="Cerrar sesion"
              >
                {mostrarTextoMenu ? 'Cerrar sesion' : 'Salir'}
              </button>
            </div>
          </Card>
        </div>

        <div
          ref={contentScrollRef}
          style={{
            flex: 1,
            marginLeft: esMobile ? '0' : `${SIDEBAR_COLLAPSED}px`,
            paddingLeft: esMobile ? '0' : '20px',
            overflowY: 'auto',
            overflowX: 'hidden',
            width: '100%',
            boxSizing: 'border-box',
            position: 'relative',
            backgroundColor: brandTheme.pageTint,
            backgroundImage: adminWatermarkLogoUrl ? 'none' : WATERMARK_PATTERN,
            backgroundRepeat: adminWatermarkLogoUrl ? 'no-repeat' : 'repeat',
            backgroundSize: adminWatermarkLogoUrl ? 'auto' : '420px 420px',
          }}
        >
          {adminWatermarkLogoUrl ? (
            <div
              aria-hidden="true"
              style={{
                position: 'sticky',
                top: 0,
                height: '100vh',
                marginBottom: '-100vh',
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 0,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '-32%',
                  left: '-32%',
                  width: '164%',
                  height: '164%',
                  transform: 'rotate(-45deg)',
                  transformOrigin: 'center',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(180px, 1fr))',
                  gridAutoRows: '160px',
                  justifyItems: 'center',
                  alignContent: 'space-evenly',
                  gap: '92px 126px',
                  opacity: 0.045,
                }}
              >
                {Array.from({ length: ADMIN_WATERMARK_LOGO_SLOTS }, (_, index) => (
                  <img
                    key={`admin-watermark-logo-${index}`}
                    src={adminWatermarkLogoUrl}
                    alt=""
                    style={{
                      width: '190px',
                      height: 'auto',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      alignSelf: 'center',
                      justifySelf: 'center',
                      userSelect: 'none',
                      WebkitUserDrag: 'none',
                    }}
                  />
                ))}
              </div>
            </div>
          ) : null}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
            }}
          >
          {location.pathname !== '/app/comercios' && <Image uploadMode={heroUploadMode} />}
          <div
            style={{
              padding: '20px',
              maxWidth: '100%',
              overflowX: 'hidden',
            }}
          >
            <CommissionDebtNotice branchId={currentBranch?.id || currentRestaurant?.id} enabled={isSupabaseConfigured && !isCreatorMode} detailed={location.pathname === '/app/pagos'} />
            <RestaurantSetupEntry />
            <Outlet />
          </div>
          </div>
        </div>
        {closingPrompt ? (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 5000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              backgroundColor: 'rgba(15, 23, 42, 0.68)',
              backdropFilter: 'blur(5px)',
            }}
          >
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="branch-closing-title"
              aria-describedby="branch-closing-description"
              style={{
                width: 'min(100%, 560px)',
                maxHeight: 'calc(100vh - 40px)',
                overflowY: 'auto',
                borderRadius: '20px',
                border: '1px solid rgba(251, 146, 60, 0.5)',
                backgroundColor: '#ffffff',
                boxShadow: '0 28px 90px rgba(15, 23, 42, 0.34)',
                padding: esMobile ? '24px 20px' : '30px',
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  minHeight: '30px',
                  padding: '0 12px',
                  borderRadius: '999px',
                  backgroundColor: '#fff7ed',
                  color: '#c2410c',
                  fontSize: '12px',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {closingPrompt.kind === 'extension'
                  ? 'Prorroga finalizada'
                  : 'Cierre programado'}
              </div>

              <h2
                id="branch-closing-title"
                style={{
                  margin: '18px 0 8px',
                  color: '#0f172a',
                  fontSize: esMobile ? '28px' : '34px',
                  lineHeight: 1.08,
                  letterSpacing: 0,
                }}
              >
                Llego el horario de cierre
              </h2>
              <p
                id="branch-closing-description"
                style={{
                  margin: 0,
                  color: '#475569',
                  fontSize: '17px',
                  lineHeight: 1.55,
                }}
              >
                {branchTitle || 'La sucursal'} alcanzo el cierre de las{' '}
                <strong style={{ color: '#0f172a' }}>
                  {formatBranchClosingTime(closingPrompt.boundaryAt)}
                </strong>
                . Confirma el cierre o extiende la atencion unos minutos mas.
              </p>

              {showClosingExtensionOptions ? (
                <div style={{ marginTop: '24px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '12px',
                      color: '#0f172a',
                      fontSize: '15px',
                    }}
                  >
                    Cuanto tiempo necesitas?
                  </strong>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(82px, 1fr))',
                      gap: '10px',
                    }}
                  >
                    {BRANCH_CLOSING_EXTENSION_OPTIONS.map((minutes) => (
                      <button
                        key={minutes}
                        type="button"
                        onClick={() =>
                          handleBranchClosingDecision('extend', minutes)
                        }
                        style={{
                          minHeight: '50px',
                          borderRadius: '12px',
                          border: '1px solid #fdba74',
                          backgroundColor: '#fff7ed',
                          color: '#9a3412',
                          fontSize: '16px',
                          fontWeight: 800,
                          cursor: 'pointer',
                        }}
                      >
                        +{minutes} min
                      </button>
                    ))}
                  </div>
                  <p
                    style={{
                      margin: '12px 0 0',
                      color: '#64748b',
                      fontSize: '13px',
                      lineHeight: 1.45,
                    }}
                  >
                    La extension vale solo para este cierre y no modifica los
                    horarios semanales.
                  </p>
                </div>
              ) : null}

              <div
                style={{
                  display: 'flex',
                  flexDirection: esMobile ? 'column-reverse' : 'row',
                  gap: '12px',
                  marginTop: '26px',
                }}
              >
                <button
                  type="button"
                  onClick={() => handleBranchClosingDecision('close')}
                  style={{
                    flex: 1,
                    minHeight: '54px',
                    borderRadius: '12px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    color: '#0f172a',
                    fontSize: '16px',
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  Aplicar cierre
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setShowClosingExtensionOptions((current) => !current)
                  }
                  aria-expanded={showClosingExtensionOptions}
                  style={{
                    flex: 1,
                    minHeight: '54px',
                    borderRadius: '12px',
                    border: '1px solid #f97316',
                    backgroundColor: '#f97316',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  {showClosingExtensionOptions
                    ? 'Ocultar opciones'
                    : 'Extender horario'}
                </button>
              </div>
            </section>
          </div>
        ) : null}
        {internalAssistantEnvironment ? (
          <LocalAiAssistant
            pathname={location.pathname}
            companyName={currentCompany?.name}
            branchName={activeBranchScope?.name}
            access={buildLocalAssistantAccess({
              userId: user?.id,
              companyId: currentCompany?.id,
              branchId: activeBranchScope?.id,
              role: currentAccessRole,
              canAccessModule,
              canPerformAction,
              profile: restaurantProfile,
              environment: internalAssistantEnvironment,
            })}
          />
        ) : null}
      </div>
    </IngredientesProvider>
  );
}

export default LayoutRestaurante;
